import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	emailScannerImageUrl,
	getLogoUrlForIntegrationProvider,
	isMetaAdsAllowedAccount,
} from '../../../../models/UiUtils';
import { useGetExternalIntegrationsQuery, useGetGoogleBusinessMetadata } from '../../../../queries';
import { useGetMetaAdsProvider } from '../../../../queries/Ads/useGetMetaAdsProvider';
import AMS360IconUrl from '../../../assets/ams360.png';
import googleBusinessProfileLogo from '../../../assets/google_business_profile_logo.png';
import hawksoftLogo from '../../../assets/hawksoft_logo.png';
import adwerxLogo from '../../../assets/logo_adwerx.png';
import clioLogo from '../../../assets/logo_clio.png';
import donorPerfectLogo from '../../../assets/logo_donorperfect.png';
import qqLogo from '../../../assets/logo_qq_catalyst.png';
import redtailLogo from '../../../assets/logo_redtail.png';
import { MetaMainLogo } from '../../svgs/icons/MetaMainLogo';
import { IntegrationCard } from '../IntegrationCard';
import { isSpecificOrganizationsManageAms360Enabled } from '../utils/isSpecificOrganizationsManageAms360';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	onManageClicked?(integration: Api.IntegrationType | Api.ConfigurableIntegrationType): void;
	styles?: StyleDeclarationValue[];
}

interface IIntegrationCardData {
	canManage?: boolean;
	img?: string;
	name?: Api.ConfigurableIntegrationType;
}

const getIntegrationCardData = (userSession: Api.UserSessionContext) => {
	const data: IIntegrationCardData[] = [
		{
			img: AMS360IconUrl,
			name: Api.ConfigurableIntegrationType.Ams360,
			canManage: isSpecificOrganizationsManageAms360Enabled(userSession),
		},
		{
			img: hawksoftLogo,
			name: Api.ConfigurableIntegrationType.HawkSoft,
		},
		{
			canManage: true,
			img: redtailLogo,
			name: Api.ConfigurableIntegrationType.Redtail,
		},
		{
			canManage: true,
			img: clioLogo,
			name: Api.ConfigurableIntegrationType.Clio,
		},
		{
			canManage: true,
			img: qqLogo,
			name: Api.ConfigurableIntegrationType.QQ,
		},
		{
			canManage: true,
			img: donorPerfectLogo,
			name: Api.ConfigurableIntegrationType.DonorPerfect,
		},
	];

	const account = userSession.account.toJs();
	if (account?.integrations?.mergeAccounting?.enabled) {
		const mergeAccountingProvider = account.integrations.mergeAccounting.mergeAccountingProvider;
		data.push({
			canManage: true,
			img: getLogoUrlForIntegrationProvider(mergeAccountingProvider),
			name: Api.ConfigurableIntegrationType.MergeAccounting,
		});
	}
	if (account?.integrations?.mergeCrm?.enabled) {
		const mergeCrmProvider = account.integrations.mergeCrm.mergeCrmProvider;
		data.push({
			canManage: true,
			img: getLogoUrlForIntegrationProvider(mergeCrmProvider),
			name: Api.ConfigurableIntegrationType.MergeCrm,
		});
	}
	return data;
};

export const IntegrationCards = ({ className, styles, onManageClicked }: IProps) => {
	const userSession = useUserSession();
	const { data: googleBusinessMetadata } = useGetGoogleBusinessMetadata({});
	const isGoogleBusinessEnabled =
		googleBusinessMetadata?.connectionState === Api.GoogleBusinessConnectionState.Connected;

	const isMetaAdsAllowed = isMetaAdsAllowedAccount(userSession.account?.id);
	const { data: metaAdsProvider } = useGetMetaAdsProvider({
		impersonationContext: userSession.impersonationContext,
		enabled: isMetaAdsAllowed,
	});
	const isMetaAdsEnabled = metaAdsProvider?.enabled ?? false;

	const onManageClickedHandler = (integration: Api.IntegrationType) => () => {
		onManageClicked?.(integration);
	};

	const getExternalIntegrationsQuery = useGetExternalIntegrationsQuery({
		impersonationContext: userSession.impersonationContext,
	});
	const enabledExternalIntegrations = React.useMemo(() => {
		return getExternalIntegrationsQuery.data?.filter(x => x.enabled) || [];
	}, [getExternalIntegrationsQuery.data]);

	const getIntegrationCards = () => {
		const accountModel = userSession.account.toJs();
		const isAdmin = userSession.isAdmin ?? false;
		const configuredIntegrationCardData = getIntegrationCardData(userSession);

		/** Only return integration cards that account has access to, and that are configured. */
		const configuredIntegrations = Object.entries(accountModel?.integrations || {})
			.filter((entry: [string, Api.IConfigurableIntegration]) => entry[1].configured)
			.map((entry: [string, Api.IConfigurableIntegration]) => {
				const integration = configuredIntegrationCardData.filter(i => {
					return i.name === entry[0];
				});

				if (integration.length) {
					// Only one card should be shown to the end user for what is considered to be the same integration.
					const isEclipseExternalIntegrationEnabled = enabledExternalIntegrations?.some(
						x => x.provider === Api.IntegrationProvider.Eclipse
					);
					const hide =
						(integration[0].name === Api.ConfigurableIntegrationType.MergeCrm &&
							enabledExternalIntegrations?.some(x => x.provider === Api.IntegrationProvider.Salesforce)) ||
						// Always hide old MergeAccounting QuickBooks - use new external integration instead
						(integration[0].name === Api.ConfigurableIntegrationType.MergeAccounting &&
							accountModel?.integrations?.mergeAccounting?.mergeAccountingProvider ===
								Api.MergeAccountingProvider.QuickBooksOnline) ||
						(integration[0].name === Api.ConfigurableIntegrationType.Clio &&
							(!accountModel?.integrations?.clio?.enabled ||
								enabledExternalIntegrations?.some(x => x.provider === Api.IntegrationProvider.Clio))) ||
						// Hide Eclipse V1 if V2 is enabled
						(integration[0].name === Api.ConfigurableIntegrationType.Eclipse && isEclipseExternalIntegrationEnabled);
					return hide ? null : (
						<IntegrationCard
							key={integration[0].name}
							comingSoon={false}
							icon={<img className={css(styleSheet.itemIcon)} src={integration[0].img} />}
							styles={[styleSheet.item]}
							canManage={integration[0].canManage}
							enabled={true}
							onManageClicked={onManageClickedHandler(integration[0].name)}
						/>
					);
				}
			});

		/** Only return email scanners that are enabled for account. */
		const scanners = Api.AllEmailScanners.filter(
			emailScanner =>
				!Api.EmailScannersBlockedFromConfiguration.has(emailScanner.id) &&
				accountModel?.features?.emailScan?.enabledScanners?.includes(emailScanner.id as Api.EmailScannerId)
		);

		const configuredEmailScanners = scanners.map(x => {
			return (
				<IntegrationCard
					canManage={userSession.isAdmin || Api.VmUtils.isIntegrationEnabledForAccount(x.id, accountModel)}
					enabled={Api.VmUtils.isIntegrationEnabledForUser(x.id, userSession.user, accountModel)}
					icon={<img className={css(styleSheet.itemIcon)} src={emailScannerImageUrl(x.id)} />}
					key={x.id}
					onManageClicked={onManageClickedHandler(x.id)}
					styles={[styleSheet.item]}
				/>
			);
		});

		const userConfiguredIntegrations = Object.entries(Api.UserConfigurableIntegrationType).map(
			(entry: [string, Api.UserConfigurableIntegrationType]) => {
				switch (entry[1]) {
					case Api.UserConfigurableIntegrationType.CRM:
						return (
							<IntegrationCard
								canManage={true}
								enabled={!!userSession?.user?.userPreferences?.bccEmail}
								icon={<div>CRM BCC Integration</div>}
								key='crm-bcc-integration'
								onManageClicked={onManageClickedHandler(Api.UserConfigurableIntegrationType.CRM)}
								styles={[styleSheet.item]}
							/>
						);
					case Api.UserConfigurableIntegrationType.AdWerx:
						return (
							<IntegrationCard
								canManage={true}
								enabled={!!userSession?.user?.userPreferences?.adwerxBccEmail}
								icon={<img className={css(styleSheet.itemIcon)} src={adwerxLogo} />}
								key='adwerx-integration'
								onManageClicked={onManageClickedHandler(Api.UserConfigurableIntegrationType.AdWerx)}
								styles={[styleSheet.item]}
							/>
						);
					case Api.UserConfigurableIntegrationType.GoogleBusiness:
						return (
							<IntegrationCard
								canManage={isAdmin}
								enabled={isGoogleBusinessEnabled}
								icon={<img className={css(styleSheet.itemIcon)} src={googleBusinessProfileLogo} />}
								key='google-business-integration'
								onManageClicked={onManageClickedHandler(Api.UserConfigurableIntegrationType.GoogleBusiness)}
								styles={[styleSheet.item, styleSheet.googleBusinessItem]}
							/>
						);
					case Api.UserConfigurableIntegrationType.MetaAds: {
						if (!isMetaAdsAllowed) {
							return null;
						}
						return (
							<IntegrationCard
								canManage={isAdmin}
								enabled={isMetaAdsEnabled}
								icon={
									<div className={css(styleSheet.metaAdsIcon)}>
										<MetaMainLogo />
									</div>
								}
								key='meta-ads-integration'
								onManageClicked={onManageClickedHandler(Api.UserConfigurableIntegrationType.MetaAds)}
								styles={[styleSheet.item]}
							/>
						);
					}
					case Api.UserConfigurableIntegrationType.PublicAPI: {
						return null;
					}
					default:
						return null;
				}
			}
		);

		return [...userConfiguredIntegrations, ...configuredIntegrations, ...configuredEmailScanners];
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} integration-cards ${className || ''}`}>
			<div className={css(styleSheet.items)}>{getIntegrationCards()}</div>
		</div>
	);
};
